import React, { useState, useEffect, useCallback, useRef } from "react";

const BASE_URL  = "https://scanmymenu-nm38.onrender.com";
// const CART_KEY  = "cartItems";
// We store placed order IDs in sessionStorage so tracker can fetch them
const ORDERS_KEY = "myOrderIds";

const STATUS_CONFIG = {
  pending:   { label: "Order Received",  emoji: "🕐", color: "#f59e0b", bg: "#fffbeb", step: 1 },
  preparing: { label: "Being Prepared",  emoji: "👨‍🍳", color: "#3b82f6", bg: "#eff6ff", step: 2 },
  ready:     { label: "Ready to Serve",  emoji: "✅", color: "#10b981", bg: "#ecfdf5", step: 3 },
  served:    { label: "Served",          emoji: "🍽️", color: "#6b7280", bg: "#f9fafb", step: 4 },
  cancelled: { label: "Cancelled",       emoji: "❌", color: "#ef4444", bg: "#fef2f2", step: 0 },
};

const STEPS = ["pending", "preparing", "ready", "served"];

// ── helpers ──────────────────────────────────────────────────────────
const getMyOrderIds = () => JSON.parse(sessionStorage.getItem(ORDERS_KEY) || "[]");

export const saveOrderId = (orderId) => {
  const ids = getMyOrderIds();
  if (!ids.includes(orderId)) {
    sessionStorage.setItem(ORDERS_KEY, JSON.stringify([...ids, orderId]));
  }
};

async function fetchOrderById(orderId) {
  const res = await fetch(`${BASE_URL}/api/orders/single/${orderId}`);
  if (!res.ok) throw new Error("not found");
  return res.json(); // { success, order }
}

export default function OrderTracker({ onClose }) {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const sseRef = useRef(null);

  // ── Fetch all my orders ─────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    const ids = getMyOrderIds();
    if (ids.length === 0) { setLoading(false); return; }
    try {
      const results = await Promise.all(ids.map(fetchOrderById));
      const valid   = results.filter((r) => r?.success).map((r) => r.order);
      // Sort newest first
      valid.sort((a, b) => {
        const da = a.createdAt?._seconds || new Date(a.createdAt).getTime() / 1000;
        const db = b.createdAt?._seconds || new Date(b.createdAt).getTime() / 1000;
        return db - da;
      });
      setOrders(valid);
    } catch (e) {
      setError("Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // ── SSE — listen for status updates ────────────────────────────
  useEffect(() => {
    const restaurantData = JSON.parse(localStorage.getItem("restaurantData") || "{}");
    const restaurantId   = restaurantData?.id;
    if (!restaurantId) return;

    const es = new EventSource(`${BASE_URL}/api/orders/live/${restaurantId}`);
    sseRef.current = es;

    es.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        if (payload.event === "status_update") {
          setOrders((prev) =>
            prev.map((o) =>
              o.orderId === payload.orderId ? { ...o, status: payload.status } : o
            )
          );
        }
      } catch (_) {}
    };
    es.onerror = () => es.close();
    return () => es.close();
  }, []);

  // ── Format time ────────────────────────────────────────────────
  const fmtTime = (ts) => {
    if (!ts) return "";
    const d = ts?._seconds
      ? new Date(ts._seconds * 1000)
      : new Date(ts);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  // ── Step progress bar ──────────────────────────────────────────
  const StepBar = ({ status }) => {
    const currentStep = STATUS_CONFIG[status]?.step || 0;
    return (
      <div style={S.stepBar}>
        {STEPS.map((s, idx) => {
          const step     = idx + 1;
          const done     = currentStep >= step;
          const active   = currentStep === step;
          const cfg      = STATUS_CONFIG[s];
          return (
            <React.Fragment key={s}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  ...S.stepDot,
                  background:   done ? cfg.color : "#e5e7eb",
                  boxShadow:    active ? `0 0 0 3px ${cfg.color}33` : "none",
                  transform:    active ? "scale(1.2)" : "scale(1)",
                }}>
                  {done ? <span style={{ fontSize: 10 }}>✓</span> : <span style={{ fontSize: 9, color: "#aaa" }}>{step}</span>}
                </div>
                <span style={{ ...S.stepLabel, color: done ? cfg.color : "#bbb", fontWeight: done ? 700 : 400 }}>
                  {cfg.emoji}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div style={{ ...S.stepLine, background: currentStep > step ? STATUS_CONFIG[STEPS[idx + 1]].color : "#e5e7eb" }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  return (
    <>
      {/* Backdrop */}
      <div style={S.backdrop} onClick={onClose} />

      {/* Slide-in panel */}
      <div style={S.panel}>

        {/* Header */}
        <div style={S.header}>
          <div>
            <h2 style={S.headerTitle}>My Orders</h2>
            <p style={S.headerSub}>Live tracking for your table</p>
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div style={S.body}>

          {loading ? (
            <div style={S.centered}>
              <div style={S.spinnerLg} />
              <p style={{ color: "#aaa", marginTop: 14 }}>Loading your orders…</p>
            </div>
          ) : error ? (
            <div style={S.centered}>
              <p style={{ fontSize: 36 }}>😕</p>
              <p style={{ color: "#aaa" }}>{error}</p>
              <button style={S.retryBtn} onClick={loadOrders}>Retry</button>
            </div>
          ) : orders.length === 0 ? (
            <div style={S.centered}>
              <p style={{ fontSize: 48, margin: 0 }}>🧾</p>
              <p style={{ color: "#aaa", marginTop: 12, fontSize: 15 }}>No orders placed yet</p>
              <p style={{ color: "#ccc", fontSize: 13, marginTop: 4 }}>
                Add items and place an order to track here
              </p>
            </div>
          ) : (
            orders.map((order) => {
              const st = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const isCancelled = order.status === "cancelled";

              return (
                <div key={order.orderId} style={{ ...S.card, opacity: isCancelled ? 0.65 : 1 }}>

                  {/* Card top row */}
                  <div style={S.cardTop}>
                    <div style={S.tableInfo}>
                      <span style={S.tableNum}>🪑 Table {order.tableNumber}</span>
                      <span style={S.orderTime}>{fmtTime(order.createdAt)}</span>
                    </div>
                    <div style={{ ...S.statusPill, background: st.bg, color: st.color }}>
                      {st.emoji} {st.label}
                    </div>
                  </div>

                  {/* Step progress — hide for cancelled */}
                  {!isCancelled && (
                    <StepBar status={order.status} />
                  )}

                  {/* Items */}
                  <div style={S.itemsList}>
                    {(order.items || []).map((item, idx) => (
                      <div key={idx} style={S.itemRow}>
                        <img
                          src={item.imageUrl || "https://via.placeholder.com/36"}
                          alt={item.name}
                          style={S.itemImg}
                          onError={(e) => { e.target.src = "https://via.placeholder.com/36"; }}
                        />
                        <span style={S.itemName}>{item.name}</span>
                        <span style={S.itemQty}>×{item.qty}</span>
                        <span style={S.itemPrice}>₹{(item.price * item.qty).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div style={S.totalRow}>
                    <span style={S.totalLabel}>Total</span>
                    <span style={S.totalValue}>₹{order.totalPrice?.toFixed(2)}</span>
                  </div>

                </div>
              );
            })
          )}
        </div>

        {/* Refresh footer */}
        {!loading && orders.length > 0 && (
          <div style={S.footer}>
            <button style={S.refreshBtn} onClick={loadOrders}>
              ↻ &nbsp;Refresh
            </button>
            <span style={S.liveTag}>🟢 Live</span>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────
const S = {
  backdrop: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 1100,
  },
  panel: {
    position: "fixed", top: 0, right: 0, bottom: 0,
    width: "min(420px, 100vw)",
    background: "#fff",
    zIndex: 1101,
    display: "flex", flexDirection: "column",
    boxShadow: "-8px 0 40px rgba(0,0,0,0.15)",
    animation: "slideInRight 0.3s cubic-bezier(.4,0,.2,1)",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "20px 20px 16px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fff",
  },
  headerTitle: { margin: 0, fontSize: 20, fontWeight: 800, color: "#111" },
  headerSub:   { margin: "3px 0 0", fontSize: 12, color: "#aaa" },
  closeBtn: {
    background: "#f5f5f5", border: "none", borderRadius: 99,
    width: 34, height: 34, fontSize: 15, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  body: {
    flex: 1, overflowY: "auto",
    padding: "14px 16px",
    display: "flex", flexDirection: "column", gap: 14,
  },
  centered: {
    flex: 1, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "60px 20px", textAlign: "center",
  },
  spinnerLg: {
    width: 36, height: 36,
    border: "4px solid #f0f0f0",
    borderTopColor: "#f7431b",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  retryBtn: {
    marginTop: 14, padding: "8px 22px",
    border: "none", borderRadius: 99,
    background: "#f7431b", color: "#fff",
    fontSize: 14, fontWeight: 700, cursor: "pointer",
  },

  // Order card
  card: {
    background: "#fafafa",
    borderRadius: 16,
    padding: "14px 14px 10px",
    border: "1.5px solid #f0f0f0",
  },
  cardTop: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: 12,
  },
  tableInfo:  { display: "flex", flexDirection: "column", gap: 2 },
  tableNum:   { fontSize: 15, fontWeight: 800, color: "#111" },
  orderTime:  { fontSize: 11, color: "#bbb" },
  statusPill: { borderRadius: 99, padding: "5px 12px", fontSize: 12, fontWeight: 700 },

  // Step bar
  stepBar: {
    display: "flex", alignItems: "center",
    marginBottom: 14, padding: "0 4px",
  },
  stepDot: {
    width: 22, height: 22, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.3s", color: "#fff", fontWeight: 800,
  },
  stepLabel: { fontSize: 14, transition: "color 0.3s" },
  stepLine:  { flex: 1, height: 3, borderRadius: 99, margin: "0 4px", marginBottom: 16, transition: "background 0.4s" },

  // Items
  itemsList: { display: "flex", flexDirection: "column", gap: 7, marginBottom: 10 },
  itemRow:   { display: "flex", alignItems: "center", gap: 8 },
  itemImg:   { width: 34, height: 34, borderRadius: 8, objectFit: "cover", flexShrink: 0 },
  itemName:  { flex: 1, fontSize: 13, fontWeight: 600, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  itemQty:   { fontSize: 12, color: "#999", fontWeight: 600 },
  itemPrice: { fontSize: 13, fontWeight: 700, color: "#111" },

  // Total
  totalRow:   { display: "flex", justifyContent: "space-between", borderTop: "1px solid #efefef", paddingTop: 8 },
  totalLabel: { fontSize: 13, color: "#888", fontWeight: 600 },
  totalValue: { fontSize: 15, fontWeight: 800, color: "#111" },

  // Footer
  footer: {
    padding: "12px 20px",
    borderTop: "1px solid #f0f0f0",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "#fff",
  },
  refreshBtn: {
    background: "#f5f5f5", border: "none", borderRadius: 99,
    padding: "7px 16px", fontSize: 13, fontWeight: 600,
    cursor: "pointer", color: "#444",
  },
  liveTag: { fontSize: 12, color: "#10b981", fontWeight: 700 },
};

/*
  Add to your global CSS:

  @keyframes slideInRight {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
*/